import type { UserStatus as ApiUserStatus } from '@/api/users';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useTabUrlSync } from '@/hooks/use-tab-url-sync';
import { useUser } from '@/hooks/use-users';
import { useTranslation } from '@kgc/i18n';
import { DetailTabPanel, DetailTabsNav } from '@kgc/ui';
import { useNavigate, useParams } from 'react-router-dom';

export function UserDetailPage() {
  const { t } = useTranslation();

  const STATUS_CONFIG: Record<ApiUserStatus, { label: string; color: string }> = {
    ACTIVE: {
      label: t('user.statusLabels.ACTIVE'),
      color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    },
    INACTIVE: {
      label: t('user.statusLabels.INACTIVE'),
      color: 'bg-gray-100 text-gray-800 dark:bg-[#1f5f6b] dark:text-[#ccfbf1]',
    },
    LOCKED: {
      label: t('user.statusLabels.LOCKED'),
      color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
    },
    PENDING_VERIFICATION: {
      label: t('user.statusLabels.PENDING_VERIFICATION'),
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
    },
  };
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Tab-state URL-syncelve (?tab=…) a @kgc/ui DetailTabsNav/DetailTabPanel foundation-höz.
  const [activeTab, setActiveTab] = useTabUrlSync('tab', 'overview');

  const { user, isLoading, error } = useUser(id);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[#0f3d47]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent" />
          <p className="mt-2 text-gray-500 dark:text-[#ccfbf1]">{t('user.loadingUser')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[#0f3d47]">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t('user.errorOccurred')}
          </h2>
          <p className="text-gray-500 dark:text-[#ccfbf1] mb-4">{error}</p>
          <Button onClick={() => navigate('/users')}>{t('common.backToList')}</Button>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('user.notFound')}</h1>
          <Button onClick={() => navigate('/users')} className="mt-4">
            {t('common.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('hu-HU');
  };

  const getStatusBadge = (status: ApiUserStatus) => {
    const config = STATUS_CONFIG[status];
    return (
      <span className={`rounded-full px-3 py-1 text-sm font-medium ${config?.color ?? ''}`}>
        {config?.label ?? status}
      </span>
    );
  };

  /** Extract initials from the name field */
  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${(parts[0] ?? '')[0] ?? ''}${(parts[1] ?? '')[0] ?? ''}`;
    }
    return (name[0] ?? '?').toUpperCase();
  };

  const tabs = [
    { key: 'overview', label: t('user.tabOverview') },
    { key: 'activity', label: t('user.tabActivity') },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f3d47]">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/users')}>
                {t('common.back')}
              </Button>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-lg font-medium text-primary">
                {getInitials(user.name)}
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white">{user.name}</h1>
                  {getStatusBadge(user.status)}
                </div>
                <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">{user.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate(`/users/${user.id}/edit`)}>
                {t('common.edit')}
              </Button>
              {user.status === 'ACTIVE' && (
                <Button
                  variant="outline"
                  className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  {t('user.suspend')}
                </Button>
              )}
              {user.status === 'LOCKED' && (
                <Button
                  variant="outline"
                  className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                >
                  {t('user.activate')}
                </Button>
              )}
            </div>
          </div>

          {/* Tabs (megosztott @kgc/ui DetailTabsNav foundation — a panelek a main-ben DetailTabPanel-ek) */}
          <DetailTabsNav
            idPrefix="user"
            value={activeTab}
            onValueChange={setActiveTab}
            tabs={tabs}
            className="mt-4 mb-0"
          />
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <DetailTabPanel
          tabKey="overview"
          activeKey={activeTab}
          idPrefix="user"
          className="grid gap-6 lg:grid-cols-3"
        >
          {/* Left column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic info */}
            <Card>
              <CardHeader>
                <CardTitle>{t('user.basicData')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                      {t('settings.fullName')}
                    </label>
                    <p className="text-gray-900 dark:text-white">{user.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                      {t('user.email')}
                    </label>
                    <p className="text-gray-900 dark:text-white">
                      <a href={`mailto:${user.email}`} className="text-blue-600 hover:underline">
                        {user.email}
                      </a>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                      {t('user.role')}
                    </label>
                    <p className="text-gray-900 dark:text-white">
                      {t(`user.roleLabels.${user.role}`) ?? user.role}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                      {t('table.status')}
                    </label>
                    <p className="text-gray-900 dark:text-white">
                      {STATUS_CONFIG[user.status]?.label ?? user.status}
                    </p>
                  </div>
                  {user.tenantId && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('user.tenantId')}
                      </label>
                      <p className="text-gray-900 dark:text-white">{user.tenantId}</p>
                    </div>
                  )}
                  {user.locationId && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                        {t('user.locationId')}
                      </label>
                      <p className="text-gray-900 dark:text-white">{user.locationId}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                      {t('common.createdAt')}
                    </label>
                    <p className="text-gray-900 dark:text-white">{formatDate(user.createdAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-[#99f6e4]">
                      {t('common.updatedAt')}
                    </label>
                    <p className="text-gray-900 dark:text-white">{formatDate(user.updatedAt)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Role info */}
            <Card>
              <CardHeader>
                <CardTitle>{t('user.role')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-lg border border-primary bg-primary/5 p-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {t(`user.roleLabels.${user.role}`) ?? user.role}
                        </p>
                        <span className="rounded bg-primary/20 px-2 py-0.5 text-xs font-medium text-primary">
                          {t('user.statusLabels.ACTIVE')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                        {t(`user.roleDescriptions.${user.role}`) ?? ''}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* Quick info */}
            <Card>
              <CardHeader>
                <CardTitle>{t('user.systemInfo')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-[#ccfbf1]">
                      {t('user.identifier')}
                    </span>
                    <span className="font-medium text-sm font-mono">{user.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-[#ccfbf1]">
                      {t('user.createdLabel')}
                    </span>
                    <span className="font-medium text-sm">{formatDate(user.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-[#ccfbf1]">
                      {t('user.modifiedLabel')}
                    </span>
                    <span className="font-medium text-sm">{formatDate(user.updatedAt)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </DetailTabPanel>

        <DetailTabPanel tabKey="activity" activeKey={activeTab} idPrefix="user">
          <Card>
            <CardHeader>
              <CardTitle>{t('user.activityLog')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="rounded-lg border p-4 dark:border-[#2d7d8a]">
                  <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                    {formatDate(user.updatedAt)}
                  </p>
                  <p className="font-medium">{t('user.profileUpdated')}</p>
                </div>
                <div className="rounded-lg border p-4 dark:border-[#2d7d8a]">
                  <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                    {formatDate(user.createdAt)}
                  </p>
                  <p className="font-medium">{t('user.userCreated')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </DetailTabPanel>
      </main>
    </div>
  );
}
